<mat-card class="wi-card" *ngIf="enabled">
  <button type="button" class="wi-toggle" (click)="toggle()" [attr.aria-expanded]="expanded">
    <div class="wi-heading">
      <span class="wi-heading-icon">
        <mat-icon>insights</mat-icon>
      </span>
      <div class="wi-toggle-text">
        <span class="wi-eyebrow">EMPLOYEE PROFILE · CONVERSATION CONTEXT</span>
        <span class="wi-title">Work Insights <span class="wi-window">Last {{ windowDays }} days</span></span>
        <span class="wi-microcopy">A calm summary of work rhythm — not a performance score.</span>
      </div>
    </div>
    <span class="wi-expand">
      <span>{{ expanded ? 'Collapse' : 'Explore' }}</span>
      <mat-icon>{{ expanded ? 'expand_less' : 'expand_more' }}</mat-icon>
    </span>
  </button>

  <div class="wi-body" *ngIf="expanded">
    <div class="wi-skeleton" *ngIf="loading" aria-label="Loading work insights">
      <ngx-skeleton-loader
        *ngFor="let row of [1, 2, 3, 4]"
        [theme]="{ height: '72px', 'margin-bottom': '10px', 'border-radius': '8px' }">
      </ngx-skeleton-loader>
    </div>

    <ng-container *ngIf="!loading && loaded">
      <div class="wi-summary-strip">
        <div class="wi-summary-item">
          <span class="wi-summary-label">Average active time</span>
          <strong>{{ formatHours(workload?.avg_active_hours_per_day ?? trendAverageHours) }}</strong>
          <span class="wi-summary-caption">per active day</span>
        </div>
        <div class="wi-summary-item">
          <span class="wi-summary-label">Tracked days</span>
          <strong>{{ workload?.distinct_active_days ?? trendMeaningfulDays }}</strong>
          <span class="wi-summary-caption">of {{ windowDays }} calendar days</span>
        </div>
        <div class="wi-summary-item">
          <span class="wi-summary-label">Deep work</span>
          <strong>{{ deepWork?.deep_work_pct == null ? '—' : (deepWork.deep_work_pct | number: '1.0-0') + '%' }}</strong>
          <span class="wi-summary-caption">of active time</span>
        </div>
        <div class="wi-summary-item">
          <span class="wi-summary-label">Vs previous {{ windowDays }} days</span>
          <strong [ngClass]="'wi-delta-' + deltaTone(periodDelta?.activePct)">
            {{ periodDelta ? formatDeltaPct(periodDelta.activePct) : '—' }}
          </strong>
          <span class="wi-summary-caption">active time change</span>
        </div>
      </div>

      <div class="wi-review-signal" *ngIf="hasReviewSignal">
        <mat-icon>flag</mat-icon>
        <span>Attention Signals marked this person for review in this window. Use it as a starting point, not a verdict.</span>
        <a [routerLink]="['/productivity-trends']" [queryParams]="{ tab: 'attention_signals' }">Open Attention Signals</a>
      </div>

      <div class="wi-tile wi-tile-trend">
        <div class="wi-tile-head">
          <div>
            <span class="wi-tile-kicker">ACTIVITY RHYTHM</span>
            <span class="wi-tile-title">Last {{ windowDays }} days</span>
          </div>
          <div class="wi-trend-total" *ngIf="trendBars.length">
            <strong>{{ formatHours(trendTotalHours, 0) }}</strong>
            <span>total active</span>
          </div>
          <mat-icon matTooltip="Each column is one calendar day, including quiet days. Color is relative to this person's typical active day, not a company healthy band.">help_outline</mat-icon>
        </div>
        <p class="wi-tile-note wi-verdict" *ngIf="trendVerdict">{{ trendVerdict }}</p>
        <div class="wi-trend-empty" *ngIf="!trendBars.length">No tracked activity in this window.</div>
        <div class="wi-trend-bars" *ngIf="trendBars.length">
          <div
            class="wi-trend-col"
            *ngFor="let bar of trendBars"
            [class.wi-weekend]="bar.isWeekend"
            [attr.title]="bar.tooltip">
            <div class="wi-trend-plot">
              <div
                class="wi-trend-bar"
                [ngClass]="'wi-shape-' + bar.shape"
                [style.height.%]="bar.heightPct"></div>
            </div>
          </div>
        </div>
        <div class="wi-trend-legend" *ngIf="trendBars.length">
          <span><i class="wi-dot wi-shape-off"></i> Quiet</span>
          <span><i class="wi-dot wi-shape-short"></i> Shorter than typical</span>
          <span><i class="wi-dot wi-shape-typical"></i> Typical for them</span>
          <span><i class="wi-dot wi-shape-long"></i> Longer than typical</span>
          <span class="wi-trend-days">{{ trendStartLabel }} – {{ trendEndLabel }}</span>
        </div>
      </div>

      <div class="wi-tile-grid wi-tile-grid-split">
        <div class="wi-tile">
          <div class="wi-tile-head">
            <span class="wi-tile-title">Typical week</span>
            <mat-icon matTooltip="Total active hours on each weekday across the last 30 days. Empty weekdays had no tracked activity.">help_outline</mat-icon>
          </div>
          <div class="wi-weekday" *ngIf="weekdayBars.length">
            <div class="wi-weekday-col" *ngFor="let day of weekdayBars" [attr.title]="day.tooltip">
              <div class="wi-weekday-plot">
                <div class="wi-weekday-bar" [style.height.%]="day.heightPct" [class.wi-shape-off]="day.hours <= 0"></div>
              </div>
              <span>{{ day.label }}</span>
            </div>
          </div>
          <p class="wi-tile-note" *ngIf="!weekdayBars.length">Not enough weekday history in this window.</p>
        </div>

        <div class="wi-tile">
          <div class="wi-tile-head">
            <span class="wi-tile-title">Where logged time went</span>
            <mat-icon matTooltip="Active and idle share one bar (logged time). Deep work and communication are contextual shares — not extra slices.">help_outline</mat-icon>
          </div>
          <ng-container *ngIf="composition; else tileNoData">
            <div class="wi-comp-row">
              <span class="wi-comp-label">This window</span>
              <span class="wi-comp-meta">{{ formatHours(composition.loggedHours, 0) }} logged</span>
            </div>
            <div class="wi-comp-bar" [attr.title]="'Active ' + (composition.activePct | number:'1.0-0') + '% · Idle ' + (composition.idlePct | number:'1.0-0') + '%'">
              <span class="wi-comp-active" [style.width.%]="composition.activePct"></span>
              <span class="wi-comp-idle" [style.width.%]="composition.idlePct"></span>
            </div>
            <div class="wi-comp-caption">
              Active {{ composition.activePct | number:'1.0-0' }}% · Idle {{ composition.idlePct | number:'1.0-0' }}%
            </div>

            <ng-container *ngIf="priorComposition">
              <div class="wi-comp-row wi-comp-row-prior">
                <span class="wi-comp-label">Previous {{ windowDays }} days</span>
                <span class="wi-comp-meta">{{ formatHours(priorComposition.loggedHours, 0) }} logged</span>
              </div>
              <div class="wi-comp-bar wi-comp-bar-prior">
                <span class="wi-comp-active" [style.width.%]="priorComposition.activePct"></span>
                <span class="wi-comp-idle" [style.width.%]="priorComposition.idlePct"></span>
              </div>
            </ng-container>

            <p class="wi-tile-note" *ngIf="periodDelta">{{ periodDelta.label }}</p>
            <p class="wi-tile-note" *ngIf="deepWork?.deep_work_pct != null">
              Deep work {{ deepWork.deep_work_pct | number:'1.0-0' }}% of active
              <ng-container *ngIf="communication?.communication_time_pct != null">
                · Communication {{ communication.communication_time_pct | number:'1.0-0' }}% of logged
              </ng-container>
            </p>
          </ng-container>
        </div>
      </div>

      <div class="wi-tile-grid wi-tile-grid-split wi-tile-grid-usage" *ngIf="appSources.length || webSources.length">
        <div class="wi-tile">
          <div class="wi-tile-head">
            <span class="wi-tile-title">Application usage</span>
            <mat-icon matTooltip="Desktop applications by active time in this window. Change is versus the previous 30 days when that comparison is available.">help_outline</mat-icon>
          </div>
          <ng-container *ngIf="appSources.length; else noAppSources">
            <ng-container *ngTemplateOutlet="sourceList; context: { $implicit: appSources }"></ng-container>
          </ng-container>
        </div>

        <div class="wi-tile">
          <div class="wi-tile-head">
            <span class="wi-tile-title">Website usage</span>
            <mat-icon matTooltip="Websites by active time in this window. Change is versus the previous 30 days when that comparison is available.">help_outline</mat-icon>
          </div>
          <ng-container *ngIf="webSources.length; else noWebSources">
            <ng-container *ngTemplateOutlet="sourceList; context: { $implicit: webSources }"></ng-container>
          </ng-container>
        </div>
      </div>

      <ng-template #sourceList let-rows>
        <div class="wi-source" *ngFor="let source of rows">
          <div class="wi-source-row">
            <span class="wi-source-name" [attr.title]="source.name">{{ source.name }}</span>
            <strong>{{ formatHours(source.hours) }}</strong>
            <span class="wi-source-delta" [ngClass]="'wi-delta-' + deltaTone(source.deltaHours)">
              {{ formatDeltaHours(source.deltaHours) }}
            </span>
          </div>
          <div class="wi-source-track">
            <span class="wi-source-fill" [style.width.%]="source.barPct"></span>
          </div>
        </div>
      </ng-template>
      <ng-template #noAppSources>
        <div class="wi-tile-empty">No tracked applications in this window.</div>
      </ng-template>
      <ng-template #noWebSources>
        <div class="wi-tile-empty">No tracked websites in this window.</div>
      </ng-template>

      <p class="wi-tile-note" *ngIf="workload?.why_flagged">{{ workload.why_flagged }}</p>

      <ng-template #tileNoData>
        <div class="wi-tile-empty">Not enough data in this window.</div>
      </ng-template>

      <div class="wi-footer">
        <a mat-stroked-button [routerLink]="['/productivity-trends']" [queryParams]="{ tab: 'employees' }">
          Open Productivity Trends
        </a>
        <button mat-stroked-button type="button" (click)="retry()">Refresh</button>
      </div>
    </ng-container>
  </div>
</mat-card>
